import { logError } from '@zg-rentals/log-error';
import { defineTracker, getAnalyticsErrorType } from '@zg-rentals/rentals-analytics';
import {
  HOTPADS_COMSCORE_PAGEVIEW_CANDIDATE,
  HOTPADS_COMSCORE_PAGEVIEW_PATH,
  HOTPADS_COMSCORE_WINDOW_PARAMS_KEY,
} from './constants';
import { getHotpadsComscoreParams, type HotpadsComscoreParams } from './params';

type HotpadsComscoreWindow = Window & {
  COMSCORE?: {
    beacon?: (params: HotpadsComscoreParams) => void;
  };
  [HOTPADS_COMSCORE_WINDOW_PARAMS_KEY]?: HotpadsComscoreParams;
};

function getWindowComscoreParams(comscoreWindow: HotpadsComscoreWindow) {
  return comscoreWindow[HOTPADS_COMSCORE_WINDOW_PARAMS_KEY] ?? getHotpadsComscoreParams({});
}

function sendPageviewCandidate(instrument: (id: string) => void) {
  void fetch(HOTPADS_COMSCORE_PAGEVIEW_PATH, {
    cache: 'no-store',
    keepalive: true,
  })
    .then(async (response) => ({ status: response.status, text: await response.text() }))
    .then(({ status, text }) => {
      if (!text.includes(HOTPADS_COMSCORE_PAGEVIEW_CANDIDATE)) {
        if (text.includes('px-captcha')) {
          instrument('pageview_candidate_px_blocked');
          return;
        }

        throw new Error(`Comscore API returned unexpected response (HTTP ${status})`);
      }

      instrument(HOTPADS_COMSCORE_PAGEVIEW_CANDIDATE);
    })
    .catch((error) => {
      logError({
        error,
        errorType: getAnalyticsErrorType('ComscoreAPIError'),
      });
    })
}

export const tracker = defineTracker((_, instrument) => {
  const comscoreWindow = window as HotpadsComscoreWindow;
  const beacon = comscoreWindow.COMSCORE?.beacon;
  if (typeof beacon !== 'function') return;

  beacon(getWindowComscoreParams(comscoreWindow));
  instrument('beacon');
  sendPageviewCandidate(instrument);
});
